import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import type {
  CardFieldCvcInputEvent,
  CardFieldExpInputEvent,
  CardFieldNumberInputEvent,
  CardFieldStyle,
  CardForm,
  ProcessOutClient,
  ProcessOutException,
} from '~/scopes/payment/processout'

import { PaymentSubmitError } from '../../form-common/types/PaymentSubmitError'
import { PROCESSOUT_VALIDATION_ERROR_TARGETS } from '../config/errors'
import { getExceptionReasonMessage } from '../helpers/getExceptionReadableMessage'
import { isProcessOutException } from '../helpers/isProcessOutException'
import {
  ProcessOutCardFieldIds,
  type ProcessOutCardVirtualFieldIds,
} from '../types/ProcessOutCardFieldIds'

import { buildVirtualForm } from './useProcessOutForm.utils'
import { useProcessOutLibrary } from './useProcessOutLibrary'

const PROCESSOUT_CARD_FIELDS_STYLE: CardFieldStyle = {
  color: '#111111',
  // TODO [PAYIN-2362] Request custom font support to ProcessOut
  fontFamily: 'BodyFont, sans-serif',
  fontSize: '14px',
  fontWeight: '300',
  '::placeholder': {
    color: '#111111',
  },
  ':focus::placeholder': {
    color: '#111111',
  },
}

export function useProcessOutForm() {
  const { getProcessOutLibrary } = useProcessOutLibrary()
  const i18n = useI18n()

  let client: ProcessOutClient | null = null
  let form: CardForm | null = null

  const virtualForm = buildVirtualForm(() => form)

  const handleFocus = (field: ProcessOutCardVirtualFieldIds) => () => {
    virtualForm[field].focused = true
  }

  const handleBlur = (field: ProcessOutCardVirtualFieldIds) => () => {
    virtualForm[field].focused = false
  }

  const handleValidationError = (
    fieldId: ProcessOutCardFieldIds,
    error: ProcessOutException,
  ) => {
    const field = virtualForm[fieldId]

    if ('invalid' in field) {
      field.invalid = true
    }
    field.error = getExceptionReasonMessage(i18n, error)
  }

  const touchField = (field: ProcessOutCardVirtualFieldIds) => {
    // For simplicity, we reset the error state when the field is touched
    virtualForm[field].touched = true
    virtualForm[field].invalid = false
    virtualForm[field].error = undefined
  }

  const resetErrors = () => {
    Object.values(ProcessOutCardFieldIds).forEach((fieldId) => {
      const field = virtualForm[fieldId]
      if ('invalid' in field) {
        field.invalid = false
      }
      field.error = undefined
    })
  }

  const handleDateInput = (event: CardFieldExpInputEvent) => {
    virtualForm['cc-exp'].filled = event.month > 0 || event.year > 2000
    touchField('cc-exp')
  }

  const handleSecurityCodeInput = (event: CardFieldCvcInputEvent) => {
    virtualForm['cc-cvc'].filled = event.cvc_length > 0
    touchField('cc-cvc')
  }

  const handleNumberInput = (event: CardFieldNumberInputEvent) => {
    virtualForm['cc-number'].filled = event.card_number_length > 0
    touchField('cc-number')
  }

  return {
    virtualForm,
    setupForm: async (publicId: string, container: HTMLElement) => {
      const { ProcessOut } = await getProcessOutLibrary()

      client = new ProcessOut(publicId)

      form = await new Promise((resolve, reject) => {
        client?.setupForm(
          container,
          {
            requireCVC: false,
            style: PROCESSOUT_CARD_FIELDS_STYLE,
          },
          resolve,
          reject,
        )
      })

      if (form) {
        form.getNumberField().on('input', handleNumberInput)
        form.getCVCField().on('input', handleSecurityCodeInput)
        form.getExpiryField().on('input', handleDateInput)

        form.getExpiryField().on('focus', handleFocus('cc-exp'))
        form.getExpiryField().on('blur', handleBlur('cc-exp'))

        form.getCVCField().on('focus', handleFocus('cc-cvc'))
        form.getCVCField().on('blur', handleBlur('cc-cvc'))

        form.getNumberField().on('focus', handleFocus('cc-number'))
        form.getNumberField().on('blur', handleBlur('cc-number'))
      }
    },
    tokenizeForm: async ({ name }: { name: string }) => {
      // Reset errors, tokenization is a new attempt that will raise new errors if any.
      resetErrors()

      try {
        return await new Promise<string>((resolve, reject) => {
          if (!client || !form) {
            reject(new Error('PO Form or client not initialized'))

            return
          }

          client?.tokenize(
            form,
            {
              name,
            },
            resolve,
            reject,
          )
        })
      } catch (error) {
        if (
          isProcessOutException(error) &&
          error.code in PROCESSOUT_VALIDATION_ERROR_TARGETS
        ) {
          const fieldId =
            PROCESSOUT_VALIDATION_ERROR_TARGETS[error.code] ??
            ProcessOutCardFieldIds.NAME

          handleValidationError(fieldId, error)

          // Throw a PaymentSubmitError without any redirection or message.
          // We want to force a 'submit-error' event without showing anything
          // to the user outside of the validation errors under the fields
          throw new PaymentSubmitError({
            cause: error,
            message: error.message,
            readableMessage: undefined,
            redirection: undefined,
            source: 'FRONT_LIBRARY_PROCESSOUT',
            type: '/errors/payment/invalid-user-input',
          })
        }
        throw error
      }
    },

    // TODO [PAYIN-3697] Move this logic to a dedicated composable/helpers
    makeCardPayment: async (
      invoiceId: string,
      token: string,
      options: { authorize_only?: boolean },
    ) => {
      return new Promise<void>((resolve, reject) => {
        if (!client || !form) {
          reject(new Error('PO Form or client not initialized'))

          return
        }

        client.makeCardPayment(invoiceId, token, options, resolve, reject)
      })
    },
  }
}
